import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Power, History, RotateCcw, X, Save, Search, Tag, MessageCircle, Copy } from "lucide-react";

/**
 * V1.29 · ANA · Biblioteca comercial global.
 * Estructura idéntica al KaiLibraryPanel pero con endpoints /ana/*,
 * añade DUPLICAR además de crear/editar/borrar/toggle/versionar.
 */
export default function AnaLibraryPanel() {
  const [templates, setTemplates] = useState([]);
  const [catalog, setCatalog] = useState({ variables_by_category: {}, variables: [], tags_suggested: [], template_categories: [] });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [tagFilter, setTagFilter] = useState("all");
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorMode, setEditorMode] = useState("create");
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [versionsOpen, setVersionsOpen] = useState(null);
  const [versions, setVersions] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, c] = await Promise.all([
        api.get(`/ana/templates`),
        api.get(`/ana/catalog`),
      ]);
      setTemplates(t.data.templates || []);
      setCatalog(c.data || {});
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => templates.filter((t) => {
    if (categoryFilter !== "all" && t.category !== categoryFilter) return false;
    if (tagFilter !== "all" && !(t.tags || []).includes(tagFilter)) return false;
    if (search.trim()) {
      const q = search.toLowerCase();
      return (t.title || "").toLowerCase().includes(q) || (t.body || "").toLowerCase().includes(q) || (t.slug || "").toLowerCase().includes(q);
    }
    return true;
  }), [templates, search, categoryFilter, tagFilter]);

  const allTags = useMemo(() => {
    const set = new Set(catalog.tags_suggested || []);
    templates.forEach((t) => (t.tags || []).forEach((tg) => set.add(tg)));
    return Array.from(set).sort();
  }, [templates, catalog]);

  const withToast = async (fn) => {
    try { return await fn(); }
    catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); throw err; }
  };

  const openCreate = () => {
    setEditorMode("create");
    setEditingTemplate({ slug: "", title: "", category: "seguimiento", body: "", tags: [], active: true });
    setEditorOpen(true);
  };
  const openEdit = (t) => {
    setEditorMode("edit");
    setEditingTemplate({ ...t, change_note: "" });
    setEditorOpen(true);
  };

  const save = async () => {
    const t = editingTemplate;
    if (!t.title.trim() || !t.body.trim()) { toast.error("Título y cuerpo son obligatorios"); return; }
    if (editorMode === "create" && !t.slug.trim()) { toast.error("Slug obligatorio"); return; }
    const call = editorMode === "create"
      ? () => api.post("/ana/templates", {
          slug: t.slug.trim().toLowerCase().replace(/[^a-z0-9_]/g, "_"),
          title: t.title, category: t.category, body: t.body, tags: t.tags, active: t.active,
        })
      : () => api.put(`/ana/templates/${t.id}`, {
          title: t.title, body: t.body, category: t.category, tags: t.tags, active: t.active,
          change_note: t.change_note || null,
        });
    try {
      await withToast(call);
      toast.success(editorMode === "create" ? "Plantilla creada" : "Plantilla actualizada (nueva versión)");
      setEditorOpen(false);
      load();
    } catch { /* handled */ }
  };

  const duplicate = async (t) => {
    try { await withToast(() => api.post(`/ana/templates/${t.id}/duplicate`)); toast.success("Plantilla duplicada"); load(); } catch { /* */ }
  };
  const toggleT = async (t) => {
    try { await withToast(() => api.post(`/ana/templates/${t.id}/toggle`)); toast.success(t.active ? "Desactivada" : "Activada"); load(); } catch { /* */ }
  };
  const remove = async (t) => {
    if (t.is_system) { toast.error("Las plantillas del sistema no se pueden borrar"); return; }
    if (!window.confirm(`¿Borrar "${t.title}"? Irreversible.`)) return;
    try { await withToast(() => api.delete(`/ana/templates/${t.id}`)); toast.success("Eliminada"); load(); } catch { /* */ }
  };
  const loadVersions = async (t) => {
    setVersionsOpen(t.id);
    try { const { data } = await api.get(`/ana/templates/${t.id}/versions`); setVersions(data.versions || []); }
    catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };
  const restoreVersion = async (t, version) => {
    if (!window.confirm(`¿Restaurar v${version}? Se crea una nueva versión.`)) return;
    try { await withToast(() => api.post(`/ana/templates/${t.id}/restore/${version}`)); toast.success("Restaurada"); setVersionsOpen(null); load(); } catch { /* */ }
  };

  const insertVariable = (varKey) => {
    if (!editingTemplate) return;
    setEditingTemplate({ ...editingTemplate, body: (editingTemplate.body || "") + `{{${varKey}}}` });
  };

  return (
    <div className="space-y-4" data-testid="ana-library-panel">
      <div className="flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-between">
        <div className="flex flex-1 gap-2 items-center max-w-3xl">
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
            <input
              type="text" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar…" data-testid="ana-library-search"
              className="w-full rounded-full border border-neutral-300 bg-white pl-10 pr-3 h-9 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>
          <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} data-testid="ana-library-category" className="rounded-full border border-neutral-300 bg-white px-3 h-9 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500">
            <option value="all">Todas las categorías</option>
            {(catalog.template_categories || []).map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={tagFilter} onChange={(e) => setTagFilter(e.target.value)} data-testid="ana-library-tag" className="rounded-full border border-neutral-300 bg-white px-3 h-9 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500">
            <option value="all">Todas las etiquetas</option>
            {allTags.map((tg) => <option key={tg} value={tg}>{tg}</option>)}
          </select>
        </div>
        <button type="button" onClick={openCreate} className="btn-primary" data-testid="ana-library-create">
          <Plus className="h-4 w-4" /> Nueva plantilla
        </button>
      </div>

      {loading ? (
        <div className="card-surface p-8 text-center text-neutral-500">Cargando…</div>
      ) : filtered.length === 0 ? (
        <div className="card-surface p-8 text-center">
          <MessageCircle className="h-8 w-8 mx-auto text-neutral-300 mb-2" />
          <div className="text-sm font-semibold mb-1">Sin plantillas para este filtro</div>
        </div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {filtered.map((t) => (
            <div key={t.id} data-testid={`ana-library-card-${t.slug}`} className="rounded-lg border border-neutral-200 bg-white p-3 flex flex-col">
              <div className="flex items-start justify-between gap-2 mb-1">
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-sm truncate">{t.title}</div>
                  <div className="text-[10px] text-neutral-500 font-mono truncate">{t.slug} · v{t.version}</div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  {t.is_system && <span className="text-[9px] font-semibold text-neutral-500 bg-neutral-100 rounded-full px-1.5 py-0.5">SYS</span>}
                  <span className={"text-[9px] font-semibold rounded-full px-1.5 py-0.5 " + (t.active ? "bg-emerald-100 text-emerald-800" : "bg-neutral-200 text-neutral-600")}>
                    {t.active ? "ACTIVA" : "INACTIVA"}
                  </span>
                </div>
              </div>
              <div className="text-xs text-neutral-700 line-clamp-3 mb-2 whitespace-pre-wrap min-h-[3.6em]">{t.body}</div>
              {(t.tags || []).length > 0 && (
                <div className="flex flex-wrap gap-1 mb-2">
                  {t.tags.map((tg) => (
                    <span key={tg} className="text-[10px] rounded-full bg-neutral-100 text-neutral-700 px-1.5 py-0.5">
                      <Tag className="h-2 w-2 inline-block mr-0.5" />{tg}
                    </span>
                  ))}
                </div>
              )}
              <div className="mt-auto flex items-center gap-1 pt-2 border-t border-neutral-100 flex-wrap">
                <button onClick={() => openEdit(t)} className="text-[11px] font-semibold text-neutral-700 hover:text-orange-700 inline-flex items-center gap-1 px-1.5 py-1 rounded hover:bg-orange-50" data-testid={`ana-library-edit-${t.slug}`}><Pencil className="h-3 w-3" />Editar</button>
                <button onClick={() => duplicate(t)} className="text-[11px] font-semibold text-neutral-700 hover:text-blue-700 inline-flex items-center gap-1 px-1.5 py-1 rounded hover:bg-blue-50" data-testid={`ana-library-duplicate-${t.slug}`}><Copy className="h-3 w-3" />Duplicar</button>
                <button onClick={() => loadVersions(t)} className="text-[11px] font-semibold text-neutral-700 hover:text-blue-700 inline-flex items-center gap-1 px-1.5 py-1 rounded hover:bg-blue-50" data-testid={`ana-library-versions-${t.slug}`}><History className="h-3 w-3" />Historial</button>
                <button onClick={() => toggleT(t)} className={"text-[11px] font-semibold inline-flex items-center gap-1 px-1.5 py-1 rounded " + (t.active ? "text-amber-700 hover:bg-amber-50" : "text-emerald-700 hover:bg-emerald-50")} data-testid={`ana-library-toggle-${t.slug}`}><Power className="h-3 w-3" />{t.active ? "OFF" : "ON"}</button>
                {!t.is_system && (
                  <button onClick={() => remove(t)} className="text-[11px] font-semibold text-red-700 hover:bg-red-50 inline-flex items-center gap-1 px-1.5 py-1 rounded ml-auto" data-testid={`ana-library-delete-${t.slug}`}><Trash2 className="h-3 w-3" /></button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {editorOpen && editingTemplate && (
        <div className="fixed inset-0 z-50 bg-neutral-900/50 flex items-center justify-center p-4" onClick={() => setEditorOpen(false)}>
          <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-neutral-200 px-4 py-3">
              <h3 className="font-semibold text-lg">{editorMode === "create" ? "Nueva plantilla ANA" : `Editar: ${editingTemplate.title}`}</h3>
              <button onClick={() => setEditorOpen(false)} className="text-neutral-500 hover:text-neutral-900"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-4 space-y-3">
              {editorMode === "create" && (
                <div>
                  <label className="text-xs font-semibold text-neutral-700">Slug (identificador único)</label>
                  <input type="text" value={editingTemplate.slug} onChange={(e) => setEditingTemplate({ ...editingTemplate, slug: e.target.value })} data-testid="ana-editor-slug" className="w-full rounded-lg border border-neutral-300 bg-white px-3 h-9 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 font-mono" />
                </div>
              )}
              <div>
                <label className="text-xs font-semibold text-neutral-700">Título</label>
                <input type="text" value={editingTemplate.title} onChange={(e) => setEditingTemplate({ ...editingTemplate, title: e.target.value })} data-testid="ana-editor-title" className="w-full rounded-lg border border-neutral-300 bg-white px-3 h-9 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs font-semibold text-neutral-700">Categoría</label>
                  <select value={editingTemplate.category} onChange={(e) => setEditingTemplate({ ...editingTemplate, category: e.target.value })} data-testid="ana-editor-category" className="w-full rounded-lg border border-neutral-300 bg-white px-3 h-9 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500">
                    {(catalog.template_categories || []).map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-neutral-700">Estado</label>
                  <select value={editingTemplate.active ? "1" : "0"} onChange={(e) => setEditingTemplate({ ...editingTemplate, active: e.target.value === "1" })} className="w-full rounded-lg border border-neutral-300 bg-white px-3 h-9 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500">
                    <option value="1">Activa</option>
                    <option value="0">Inactiva</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-neutral-700">Etiquetas</label>
                <div className="flex flex-wrap gap-1 mt-1">
                  {allTags.map((tg) => {
                    const on = (editingTemplate.tags || []).includes(tg);
                    return (
                      <button key={tg} type="button" onClick={() => {
                        const s = new Set(editingTemplate.tags || []);
                        if (on) s.delete(tg); else s.add(tg);
                        setEditingTemplate({ ...editingTemplate, tags: Array.from(s) });
                      }} className={"text-[11px] rounded-full px-2 py-0.5 border " + (on ? "bg-orange-100 border-orange-300 text-orange-800" : "bg-white border-neutral-300 text-neutral-600 hover:bg-neutral-50")}>{tg}</button>
                    );
                  })}
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-neutral-700">Cuerpo del mensaje</label>
                <textarea value={editingTemplate.body} onChange={(e) => setEditingTemplate({ ...editingTemplate, body: e.target.value })} data-testid="ana-editor-body" className="w-full min-h-[130px] rounded-lg border border-neutral-300 bg-white p-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-orange-500" placeholder="Hola {{nombre}}, ..." />
                <div className="mt-2">
                  <div className="text-[10px] font-semibold text-neutral-500 uppercase mb-1">Insertar variable:</div>
                  <div className="flex flex-wrap gap-1 max-h-32 overflow-y-auto">
                    {Object.entries(catalog.variables_by_category || {}).map(([cat, vars]) => (
                      <div key={cat} className="w-full">
                        <div className="text-[9px] font-semibold text-neutral-400 uppercase mt-1">{cat}</div>
                        <div className="flex flex-wrap gap-1">
                          {vars.map((v) => (
                            <button key={v.key} type="button" onClick={() => insertVariable(v.key)} className="text-[10px] rounded-full bg-neutral-100 hover:bg-orange-100 hover:text-orange-800 px-2 py-0.5 font-mono transition-colors" title={v.label} data-testid={`ana-editor-insert-${v.key}`}>{`{{${v.key}}}`}</button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              {editorMode === "edit" && (
                <div>
                  <label className="text-xs font-semibold text-neutral-700">Nota de cambio (opcional)</label>
                  <input type="text" value={editingTemplate.change_note || ""} onChange={(e) => setEditingTemplate({ ...editingTemplate, change_note: e.target.value })} placeholder="Qué has cambiado" className="w-full rounded-lg border border-neutral-300 bg-white px-3 h-9 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
                </div>
              )}
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-neutral-200 px-4 py-3 bg-neutral-50">
              <button onClick={() => setEditorOpen(false)} className="btn-secondary">Cancelar</button>
              <button onClick={save} className="btn-primary" data-testid="ana-editor-save"><Save className="h-4 w-4" /> Guardar (PIN sudo)</button>
            </div>
          </div>
        </div>
      )}

      {versionsOpen && (
        <div className="fixed inset-0 z-50 bg-neutral-900/50 flex items-center justify-center p-4" onClick={() => setVersionsOpen(null)}>
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-neutral-200 px-4 py-3">
              <h3 className="font-semibold text-lg flex items-center gap-2"><History className="h-4 w-4" />Historial de versiones</h3>
              <button onClick={() => setVersionsOpen(null)} className="text-neutral-500 hover:text-neutral-900"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-4 space-y-2" data-testid="ana-versions-list">
              {versions.length === 0 ? <p className="text-sm text-neutral-500">Sin versiones anteriores.</p> : versions.map((v) => (
                <div key={v.id} className="rounded-lg border border-neutral-200 p-3">
                  <div className="flex items-center justify-between mb-1">
                    <div className="text-sm font-semibold">v{v.version}</div>
                    <button onClick={() => { const t = templates.find((x) => x.id === versionsOpen); if (t) restoreVersion(t, v.version); }} data-testid={`ana-version-restore-${v.version}`} className="text-xs font-semibold text-orange-700 hover:text-orange-900 inline-flex items-center gap-1"><RotateCcw className="h-3 w-3" />Restaurar</button>
                  </div>
                  <div className="text-[11px] text-neutral-500">{new Date(v.snapshot_at).toLocaleString("es-ES")} · por {v.snapshot_by}{v.change_note && ` · "${v.change_note}"`}</div>
                  <div className="text-xs whitespace-pre-wrap font-mono bg-neutral-50 p-2 rounded mt-2 border border-neutral-200">{v.body}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
